/**
 * Cases index page.
 *
 * Gallery of clinical cases demonstrating successful treatments.
 * All current records are DEMO data and carry a visible notice.
 * Imagery is gated on patient consent (mayPublishCaseImages).
 */

import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { CASES } from "@/content/cases";
import { getService } from "@/content/services";
import { clinicNode } from "@/lib/schema";
import { localizedPath } from "@/lib/locale";
import { motion } from "framer-motion";
import { AlertCircle, Lock } from "lucide-react";

interface CasesIndexPageProps {
  locale: Locale;
}

export default function CasesIndexPage({ locale }: CasesIndexPageProps) {
  const { t } = useLanguage();

  const title = locale === "ar" ? "حالات سريرية" : "Clinical Cases";
  const description =
    locale === "ar"
      ? "اطّلع على نتائج علاجاتنا الناجحة من خلال حالات سريرية قبل وبعد"
      : "See successful treatment outcomes from our clinical cases";

  const demoNoticeTitle =
    locale === "ar" ? "بيانات تجريبية" : "Demo Data";
  const demoNoticeText =
    locale === "ar"
      ? "الحالات السريرية أدناه بيانات تجريبية مؤقتة للمراجعة. الحالات الحقيقية معتمدة من المريض ستحل محلها قريبًا."
      : "The clinical cases below are temporary demo data for review. Real patient-consented cases will be coming soon.";

  const consentNoticeTitle =
    locale === "ar" ? "الخصوصية والموافقة" : "Privacy & Consent";
  const consentNoticeText =
    locale === "ar"
      ? "الصور الفوتوغرافية السريرية تُنشر فقط بموافقة موثقة من المريض. يحترم فريقنا سرية المريض وخصوصيته بشكل كامل."
      : "Clinical photographs are published only with documented patient consent. We fully respect patient privacy and confidentiality.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/cases`}
        locale={locale}
        schema={clinicNode(locale)}
      />
      <main className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">{title}</h1>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl">
            {description}
          </p>
        </motion.div>

        {/* Demo Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-lg border border-amber-600/50 bg-amber-900/20 flex gap-3 items-start"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-amber-300 mb-1">{demoNoticeTitle}</h2>
            <p className="text-sm text-amber-200/90">{demoNoticeText}</p>
          </div>
        </motion.div>

        {/* Consent Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 p-4 rounded-lg border border-slate-600/50 bg-slate-900/30 flex gap-3 items-start"
        >
          <Lock className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-slate-300 mb-1">{consentNoticeTitle}</h2>
            <p className="text-sm text-slate-400">{consentNoticeText}</p>
          </div>
        </motion.div>

        {/* Cases Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CASES.map((c, idx) => {
            const service = getService(c.service);
            return (
              <motion.a
                key={c.slug}
                href={localizedPath(locale, `/cases/${c.slug}`)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -4 }}
                className="group block overflow-hidden rounded-xl border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all duration-300"
              >
                {/* Placeholder for imagery (consent gated) */}
                <div className="aspect-video bg-slate-800 flex items-center justify-center border-b border-slate-700">
                  <div className="text-center">
                    <Lock className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">
                      {locale === "ar"
                        ? "صور محمية"
                        : "Images protected by consent"}
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-white mb-2">
                    {c.title[locale]}
                  </h3>
                  {service && (
                    <p className="text-sm text-primary font-medium mb-3">
                      {service.name[locale]}
                    </p>
                  )}
                  <p className="text-sm text-slate-400">
                    {locale === "ar"
                      ? "اطّلع على التفاصيل ←"
                      : "View case details →"}
                  </p>
                </div>
              </motion.a>
            );
          })}
        </div>
      </main>
    </>
  );
}
